import React, { useMemo } from 'react';
import { Gutter, Layout, Loader, useScrollRegionReserve } from '@axiom/ui';
import type { FormPropsChildren } from '@axiom/ui';
import type { User } from '@axiom/validation';

import type { ChatForm } from '../../../schema/spark/chat-form-schema';
import type { ChatTurnGroup } from '../../types/chat.types';
import { ChatElement } from '../../enums/chat-element';
import { ChatMetric } from '../../enums/chat-metric';
import { ChatMessageOriginators } from '../../enums/chat-message-originators';
import { useSparkSession } from '../../hooks/useSparkSession';
import { ChatScreenUtil } from '../../utils/chat-screen-util';

import { usePinToTop } from './usePinToTop';
import { ChatTurn } from './ChatTurn';
import { ChatComposer } from './ChatComposer';

const { COLUMN_PADDING, LOG_GUTTER } = ChatMetric;
const { SCROLL_CONTENT } = ChatElement;

const GREETINGS = [
  'Where should we begin?',
  'Ready when you are.',
  'Hey, $name. Ready to dive in?',
  'What’s on your mind today?',
  'How can I help, $name?',
] as const;

export type ChatProps = {
  fireSubmit: FormPropsChildren<ChatForm>['fireSubmit'];
  name: string;
  user: User;
  values: FormPropsChildren<ChatForm>['values'];
};

/**
 * Chat - I lay out the reading column: the transcript grouped into turns, and the composer
 * beneath it. ONLY concerned with what is on screen - no sending, no state of its own.
 */
export const Chat = ({ fireSubmit, name, user, values }: ChatProps) => {
  const {
    session: { status, transcript },
  } = useSparkSession();
  const { chatLog, pinnedPromptId, restoredChatId, showCandidates } =
    transcript;
  const { isLoading } = status;

  const greeting = useMemo(
    () =>
      // `firstName` can be null; the greeting must still read as English.
      (
        GREETINGS[Math.floor(Math.random() * GREETINGS.length)] ?? GREETINGS[0]
      ).replace('$name', user.firstName ?? 'there'),
    [user.firstName]
  );
  const turns = useMemo(() => {
    const grouped: ChatTurnGroup[] = [];

    chatLog.forEach((entry, idx) => {
      const current = grouped.at(-1);

      // A restored log may open on something other than a prompt.
      if (entry.messageOriginator === ChatMessageOriginators.USER || !current) {
        grouped.push({ id: entry.id, entries: [{ entry, idx }] });

        return;
      }

      current.entries.push({ entry, idx });
    });

    return grouped;
  }, [chatLog]);

  // Restore aligns the last turn's top; scroll-to-bottom breaks when fields grow after mount.
  const isRestore = !pinnedPromptId && Boolean(restoredChatId);
  const scrollKey = pinnedPromptId ?? restoredChatId;
  const scrolledTurnId =
    pinnedPromptId ?? (isRestore ? turns.at(-1)?.id : null);
  const scrolledTurnRef = usePinToTop<HTMLDivElement>({
    key: scrollKey,
    instant: isRestore,
  });

  const { hasRoom: lastTurnHasRoom, ref: lastTurnRef } =
    useScrollRegionReserve<HTMLDivElement>({
      key: scrollKey,
    });
  const hasChat = chatLog.length > 0 || isLoading;

  const bottomInset = ChatScreenUtil.bottomInset({ hasChat });

  return (
    <Gutter
      bottom={bottomInset}
      horizontal={COLUMN_PADDING}
      name={name}
      top={COLUMN_PADDING}
    >
      <Layout position="center">
        <Layout
          direction="vertical"
          maxWidth="768px"
          minHeight={ChatScreenUtil.screen({ hasChat })}
          name={SCROLL_CONTENT}
          position={hasChat ? 'space-between' : 'center'}
          verticalGutter={LOG_GUTTER}
        >
          {hasChat ? (
            <Layout direction="vertical" name="CHAT_LOG" verticalGutter="16px">
              {turns.map((turn, turnIndex) => {
                const isLastTurn = turnIndex === turns.length - 1;

                // Combined here so `ChatTurn`'s `memo` can bail out.
                return (
                  <ChatTurn
                    hasRoom={!!isLastTurn && lastTurnHasRoom}
                    isLastTurn={isLastTurn}
                    isScrolledTurn={turn.id === scrolledTurnId}
                    key={turn.id}
                    lastTurnRef={lastTurnRef}
                    scrolledTurnRef={scrolledTurnRef}
                    showCandidates={showCandidates}
                    showLoading={!!isLoading && isLastTurn}
                    turn={turn}
                  />
                );
              })}

              {/* A conversation loading into an empty log. */}
              {isLoading && turns.length === 0 ? (
                <Loader name="LOADING" type="dots" />
              ) : null}
            </Layout>
          ) : null}
          <ChatComposer
            fireSubmit={fireSubmit}
            greeting={greeting}
            hasChat={hasChat}
            values={values}
          />
        </Layout>
      </Layout>
    </Gutter>
  );
};
